// KS4 Student Book — Senior Programme (Ages 14–16)
// Phase 2 launch: 3 starter chapters with source-led inquiry suitable
// for GCSE-style discussion. Full 12-chapter expansion (Buddhism &
// Citizenship, Equality Rights & Responsibility, Source-based Inquiry)
// to follow.

export const META = {
  series: "Ambedkarite Buddhist Educational Series — UK Edition",
  title: "Senior Programme · Key Stage 4",
  subtitle: "Ages 14–16 · Source-based inquiry into Buddhism, citizenship and equality",
  edition: "Phase 2 · Sample Edition · 2026",
  status: "3 chapters published · 9 more in development",
};

export const CHAPTERS = [
  {
    n: 1,
    title: "Why did Babasaheb choose Buddhism?",
    bigIdea: "A conversion is a public argument as much as a private decision. Ambedkar's was both.",
    vocab: [
      ["conversion", "the deliberate change of one's religious or philosophical commitment."],
      ["Navayāna", "literally 'new vehicle' — Ambedkar's reform-minded interpretation of Buddhism."],
      ["dhamma", "the teaching of the Buddha; also the moral order of the universe."],
      ["emancipation", "being freed from legal, social or economic restriction."],
    ],
    storyTitle: "Nagpur, 14 October 1956",
    story: [
      "On a stage in Nagpur, in front of half a million people, Dr B. R. Ambedkar took the three refuges and recited the five precepts.",
      "He had spent two decades preparing for this moment.",
      "In 1935 he had declared, '<em>I was born a Hindu but I will not die one.</em>' He spent the years that followed studying every major faith.",
      "He read deeply about Christianity, Islam and Sikhism. He kept returning to one tradition that, he argued, taught ethics without supernatural compulsion: the teachings of the Buddha.",
      "He distilled his own reading into a book titled <em>The Buddha and his Dhamma</em>, published the year of his conversion.",
      "He chose Buddhism, he said, because it asked nothing on faith. It started with one premise — that suffering has causes — and offered a moral path anyone could walk.",
      "He saw it as the only Indian tradition that explicitly rejected caste hierarchy at its philosophical root.",
      "Six weeks after Nagpur, on 6 December 1956, Babasaheb died.",
    ],
    source: {
      quote: "\"My final words of advice to you are: educate, agitate and organise; have faith in yourself.\"",
      attribution: "Dr B. R. Ambedkar, All-India Depressed Classes Conference, 1942",
    },
    activityTitle: "Source comparison exercise",
    activity: [
      "In pairs, read three short extracts (provided by your teacher): (a) Ambedkar 'Annihilation of Caste' (1936), (b) Dhammapada verses 165–166, (c) UK Equality Act 2010 Section 4.",
      "For each extract, identify (i) the source's core claim about equality, (ii) the practical action it asks for, (iii) the evidence it uses.",
      "Build a comparison table on a single sheet of A4.",
      "Be ready to defend in class which two extracts are 'closest in spirit' and which is the outlier.",
    ],
    talkPrompts: [
      "Is a religious conversion a private spiritual act, a public political statement, or both?",
      "Ambedkar accepted only what could be argued for. Where does that leave faith?",
      "How does Nagpur 1956 connect — philosophically — to the UK's Equality Act 2010?",
    ],
    writtenTask: "In 400 words, evaluate the claim: <em>'Babasaheb's conversion was primarily an act of political resistance, not religious faith.'</em> Refer to at least one source extract and at least one piece of counter-argument.",
  },
  {
    n: 2,
    title: "The Ethics of Mindfulness",
    bigIdea: "Mindfulness without ethics is just calmness. Mindfulness with ethics is a way of life.",
    vocab: [
      ["sila", "the ethical conduct strand of the Buddhist path."],
      ["samadhi", "the meditation / concentration strand of the path."],
      ["paññā", "wisdom — the insight strand of the path."],
      ["secular mindfulness", "modern stress-reduction practice derived from Buddhist meditation but stripped of religious framing."],
    ],
    storyTitle: "When Mindfulness Became Big Business",
    story: [
      "By 2025, the global mindfulness industry was worth several billion pounds.",
      "Apps, school programmes, NHS courses, corporate retreats — all rooted in techniques the Buddha taught.",
      "Most modern programmes focus on a single benefit: <em>stress reduction</em>.",
      "But the traditional Buddhist path doesn't separate meditation from ethics. It binds three strands together: <strong>sila</strong> (ethical conduct), <strong>samadhi</strong> (meditation) and <strong>paññā</strong> (wisdom).",
      "Strip one strand out and the path becomes unbalanced.",
      "A calm trader still trades. A relaxed bully is still a bully.",
      "The traditional question is harder than 'are you calmer?'. It is: <em>are you becoming a kinder, fairer, more honest person?</em>",
    ],
    source: {
      quote: "\"All wholesome states have their root in compassion.\"",
      attribution: "Saṃyutta Nikāya, paraphrase",
    },
    activityTitle: "Ethics audit of a wellbeing programme",
    activity: [
      "In groups, choose a real example: a school mindfulness scheme, a corporate wellbeing app, or an NHS course.",
      "Identify which of the three strands (sila / samadhi / paññā) it primarily targets.",
      "Identify what is gained — and what risk arises — by leaving the other two strands out.",
      "Propose <strong>one</strong> small change that would restore some ethical content to the programme without imposing a religion.",
    ],
    talkPrompts: [
      "Can secular mindfulness ever be 'ethically neutral'?",
      "Why might a school choose to teach mindfulness but <em>not</em> the ethics that traditionally surround it?",
      "Is there a UK educational subject where the ethics strand is currently the strongest? Where it is weakest?",
    ],
    writtenTask: "Write a 400-word essay: <em>'Stress reduction is a thin reading of mindfulness.'</em> Argue for OR against the claim, drawing on the three-strand model.",
  },
  {
    n: 3,
    title: "Charities, Public Benefit and Modern Democracy",
    bigIdea: "Charities exist because some forms of public good cannot be delivered by markets or by the state alone.",
    vocab: [
      ["CIO", "Charitable Incorporated Organisation — a UK legal form for charities."],
      ["public benefit", "the legal test a charity must meet under the Charities Act 2011."],
      ["trustee", "an unpaid volunteer responsible in law for a charity's governance and finances."],
      ["safeguarding", "the duty to protect children and vulnerable adults from harm."],
    ],
    storyTitle: "A Sangha Becomes a Charity",
    story: [
      "Until quite recently, the Ambedkarite Buddhist Society UK was an unincorporated community group — a few volunteers organising meditations, talks and events.",
      "In 2026 the group's trustees made a decision: to apply to the UK Charity Commission as a <strong>Charitable Incorporated Organisation</strong> (CIO).",
      "Becoming a charity is not a paperwork formality. It is a public commitment.",
      "A charity must define its <em>objects</em> — the things it exists to do — and prove that those objects deliver <strong>public benefit</strong>.",
      "It must appoint trustees, who become personally responsible in law for the charity's work.",
      "It must publish accounts. It must safeguard children. It must not be partisan in politics.",
      "In return, the public extends to the charity its trust — and a small set of legal protections (Gift Aid, limited liability, the use of 'registered charity' on the letterhead).",
      "This is a quiet, important pattern of British civic life: groups choosing to take on responsibility in exchange for the public's trust.",
    ],
    source: {
      quote: "\"A charity must be established for charitable purposes only, which are for the public benefit.\"",
      attribution: "Charities Act 2011, Section 1",
    },
    activityTitle: "Trustees in five minutes",
    activity: [
      "In groups of four, imagine you are the founding trustees of a new community sports charity.",
      "Draft <strong>three charitable objects</strong> in one sentence each.",
      "Identify <strong>two public-benefit tests</strong> your charity would pass.",
      "Identify <strong>one safeguarding risk</strong> and one policy that would mitigate it.",
      "Present to the class in two minutes. The class votes on which charity it would 'register'.",
    ],
    talkPrompts: [
      "Why are charities legally required to be politically neutral?",
      "What is the difference between a 'community group' and a 'registered charity'? Why does the difference matter?",
      "Could a digital platform — like the one you're reading this on — qualify as charitable infrastructure?",
    ],
    writtenTask: "In 500 words, evaluate the claim: <em>'Charitable status in the UK is a privilege earned, not a right.'</em> Use the public-benefit test and at least one real example.",
  },
];
